import { CloseCircleOutlined } from "@ant-design/icons"
import { Select } from "antd"
import React, { useEffect, useMemo, useState } from "react"

import { useAppSelector } from "../../hooks/use-app-selector"
import { cn } from "../../utils/cn"
import { filterOptionsByVisibility } from "../../utils/filterOptionsByVisibility.ts"
import {
  type FallbackLabels,
  mergeSelectedIntoOptions,
  modeAcceptsFreeText
} from "../../utils/select/resolve-options.ts"

interface SelectComponentProps extends React.ComponentProps<typeof Select> {
  optionFilterType?: string
  fallbackLabels?: FallbackLabels
  fallbackLabel?: string
}

export const SelectComponent = React.forwardRef<any, SelectComponentProps>(
  (
    {
      className,
      options,
      optionFilterType,
      onChange,
      allowClear = true,
      fallbackLabels,
      fallbackLabel,
      ...props
    },
    ref
  ) => {
    const isDisabledTransition = useAppSelector(
      (state) => state.nav.disabledTransition
    )
    const isMobile = useAppSelector((state) => state.nav.isMobile)
    const [open, setOpen] = useState<boolean>(false)

    // Visibility filtering can strip the very option a stored value points at,
    // so the selected values are merged back in after it runs. Memoised: a fresh
    // array on every render makes antd re-flatten its options each time.
    const filteredOptions = useMemo(() => {
      const visible = optionFilterType
        ? filterOptionsByVisibility(options, optionFilterType)
        : options
      if (modeAcceptsFreeText(props?.mode)) return visible
      return mergeSelectedIntoOptions(
        visible,
        props?.value,
        fallbackLabels,
        fallbackLabel
      )
    }, [
      options,
      optionFilterType,
      props?.mode,
      props?.value,
      fallbackLabels,
      fallbackLabel
    ])

    const handleOpenChange = (open: boolean) => {
      setOpen(open)

      if (open) {
        document.body.style.overflow = "hidden"
      } else {
        document.body.style.overflow = "unset"
      }

      props?.onOpenChange?.(open)
    }

    const handleOnChange: SelectComponentProps["onChange"] = (value) => {
      if (!value) return onChange?.(value ?? null)
      onChange?.(value)
    }

    const combinedDropdownRender = (menu: React.ReactElement) => {
      const mobileWrapper = (content: React.ReactElement) => (
        <div className="[&>*>.rc-virtual-list-holder]:overscroll-none">
          {content}
        </div>
      )

      return props?.popupRender
        ? props.popupRender(mobileWrapper(menu))
        : mobileWrapper(menu)
    }

    useEffect(() => {
      const preventScroll = (event: WheelEvent | TouchEvent) => {
        if (open) {
          event.preventDefault()
        }
      }

      window.addEventListener("wheel", preventScroll, { passive: false })
      window.addEventListener("touchmove", preventScroll, { passive: false })

      return () => {
        window.removeEventListener("wheel", preventScroll)
        window.removeEventListener("touchmove", preventScroll)
      }
    }, [open])

    useEffect(() => {
      if (!isMobile) return
      return () => {
        document.body.style.overflow = "unset"
      }
    }, [isMobile])

    useEffect(() => {
      setOpen(!!props?.open)
    }, [props?.open])

    return (
      <Select
        // suffixIcon={
        //   <Icon type="chevron_down_small" style={{ pointerEvents: "none" }} />
        // }
        onChange={handleOnChange}
        // menuItemSelectedIcon={<Icon type="circle_check" />}
        ref={ref || null}
        className={cn(
          className,
          "select [&_.ant-select-selection-placeholder]:text-[#9E9E9E]",
          {
            "[&_.ant-select-clear]:scale-125": isMobile,
            "disabled-transition": isDisabledTransition
          }
        )}
        allowClear={allowClear}
        {...{
          ...(options ? { options: filteredOptions || [] } : {}),
          ...props,
          ...(isMobile
            ? {
                // getPopupContainer: (trigger) =>
                //   trigger?.parentNode || document.body,
                onOpenChange: handleOpenChange,
                clearIcon: (
                  <button className={"z-10"}>
                    <CloseCircleOutlined />
                  </button>
                ),
                popupRender: combinedDropdownRender
              }
            : {})
        }}
      />
    )
  }
)

SelectComponent.displayName = "Select"

export default SelectComponent
